/* eslint-disable react/no-unknown-property */
/* eslint-disable jsx-a11y/anchor-is-valid */
/** @jsxImportSource @emotion/react */
import { batch, useDispatch, useSelector } from 'react-redux';
import { useTheme } from '@emotion/react';
import { useEffect, useRef } from 'react';
import Box from '@mui/material/Box';
import MuiButton from '@mui/material/Button';
import Container from '@mui/material/Container';
import Grid from '@mui/material/Grid';
import Button from '@Button';
import Text from '@Text';
import DOMPurify from 'isomorphic-dompurify';
import striptags from 'striptags';
import makeNewsletterStyles from './makeNewsletterStyles';
import { getUrl } from '../../Helpers/submission';
import {
  getEmailAddress,
  getMatchType,
  getNewsletterContent,
} from './newsletter.helpers';
import NewsletterSubscriptionStatus from '../../Data/NewsletterSubscriptionStatus';
import { exposeUserActionEvent } from '../../Helpers/events';
import { subscribeToNewsletterAction } from '../../Lib/redux/sagas/Newsletter/subscribeToNewsletter';
import { setShouldNavigateForward } from '../../Lib/redux/slices/navigationControls';
import { NewsletterConfig } from '../../Data/NewsletterBrands';
import NewsletterConsentStatement from './NewsletterConsentStatement';
import useGetExperimentName from '../../Hooks/useGetExperimentName/useGetExperimentName';

const Newsletter = ({ componentId, iFrameId }) => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const classes = makeNewsletterStyles({ theme });
  const newsletterSignupContainerRef = useRef();
  const component = useSelector(
    (i) => i.components[`component_${componentId}`]
  );
  const components = useSelector((store) => store.components);
  const { privacyPolicy, pageUrl, countryCode, subcategoryName } = useSelector(
    (store) => store.formConfig
  );
  const { subscriptionStatus, dynamicNewsletterName } = useSelector(
    (store) => store.newsletter
  );
  const { statement: legalStatement } = useSelector(
    (store) => store.legalStatement
  );
  const { id: submissionId } = useSelector((store) => store.submission);
  const url = getUrl(pageUrl);
  const email = getEmailAddress(components);
  const { newsletters: newsletterConsentStatements } = useSelector(
    (store) => store.legalStatement
  );
  const { isDynamicHeightEnabled } = useSelector(
    (store) => store.optionalFormConfig
  );
  const {
    text,
    subheading,
    brand,
    sign_up_text: signUpText,
    skip_text: skipText,
  } = getNewsletterContent({
    text: component.component.text,
    subheading: component.component.subheading,
    additionalSettings: component.component.additionalSettings,
    dynamicNewsletterName,
  });

  const matchType = getMatchType(
    dynamicNewsletterName,
    component.component.additionalSettings.is_dynamic
  );

  const buttonExperimentName = useGetExperimentName({
    componentId,
    componentName: 'Button',
  });
  const textExperimentName = useGetExperimentName({
    componentId,
    componentName: 'Text',
  });

  let sanitisedSubheading;
  if (!DOMPurify.isSupported) {
    // If DOMPurify is not supported it defaults to the original string.
    // Instead strip all tags from string to prevent script injection.
    sanitisedSubheading = striptags(subheading);
  } else {
    // DOMPurify strips all tags other than those specified as well as any attributes such as onload.
    sanitisedSubheading = DOMPurify.sanitize(subheading, {
      ALLOWED_TAGS: ['i', 'strong', 'b'],
    });
  }

  const newsletterId = NewsletterConfig[brand].id;

  const dataFields = {
    consentStatement: legalStatement,
    privacyPolicy: privacyPolicy.link,
    submitUrl: url,
    submissionId,
    country: countryCode,
    subcategoryName,
    originId: process.env.NEXT_PUBLIC_SUBMISSION_ORIGIN_ID,
  };

  useEffect(() => {
    exposeUserActionEvent({
      type: 'newsletterSignupShown',
      actionDetails: {
        newsletter: newsletterId,
        matchType,
      },
      iFrameId,
    });
  }, []);

  useEffect(() => {
    if (
      [
        NewsletterSubscriptionStatus.SUBSCRIBED,
        NewsletterSubscriptionStatus.ERROR,
      ].includes(subscriptionStatus)
    ) {
      dispatch(setShouldNavigateForward({ shouldNavigateForward: true }));
    }
  }, [subscriptionStatus]);

  const handleSubscriptionClick = () => {
    exposeUserActionEvent({
      type: 'subscriptionClick',
      actionDetails: {
        newsletter: newsletterId,
        matchType,
      },
      iFrameId,
    });
    batch(() => {
      dispatch(subscribeToNewsletterAction(newsletterId, email, dataFields));
    });
  };

  const handleDoNotSubscribeClick = () => {
    exposeUserActionEvent({
      type: 'doNotSubscribeClick',
      actionDetails: {
        newsletter: newsletterId,
        matchType,
      },
      iFrameId,
    });
    dispatch(setShouldNavigateForward({ shouldNavigateForward: true }));
  };

  const LogoComponent = NewsletterConfig[brand].logo;

  const newsletterConsentStatement = newsletterConsentStatements.find(
    (consentStatement) => consentStatement.newsletter === newsletterId
  ).statement;

  return (
    <div
      className="newsletterSignupTransitionContainer"
      data-tf-element-role="offer"
    >
      <Grid container ref={newsletterSignupContainerRef} direction="column">
        <Container css={classes.newsletterTextContainer}>
          <div css={classes.brandLogo}>
            <LogoComponent />
          </div>

          <Box css={classes.heading}>
            <Text variant="question" experimentName={textExperimentName}>
              {text}
            </Text>
          </Box>
          <Box css={classes.subheading}>
            <Text variant="text" experimentName={textExperimentName}>
              {/* dangerouslySetInnerHTML should only be used with sanitised text. */}
              <span dangerouslySetInnerHTML={{ __html: sanitisedSubheading }} />
            </Text>
          </Box>
          <img
            // eslint-disable-next-line react/no-unknown-property
            css={classes.brandImage}
            src={NewsletterConfig[brand].image}
            alt={NewsletterConfig[brand].imageAltText}
          />
        </Container>
        {!isDynamicHeightEnabled && (
          <Container maxWidth="md">
            <NewsletterConsentStatement
              statement={newsletterConsentStatement}
            />
          </Container>
        )}
        <Container maxWidth="md" css={classes.newsletterFooterContainer}>
          <Box
            css={classes.subscribeButtonContainer}
            data-testid="subscribeButtonContainer"
          >
            <Button
              label={signUpText}
              onClick={handleSubscriptionClick}
              loading={
                subscriptionStatus === NewsletterSubscriptionStatus.IN_PROGRESS
              }
              dataTestId="subscribecta"
              experimentName={buttonExperimentName}
            />
          </Box>
          <Box data-testid="noThanksButtonContainer">
            <MuiButton
              css={classes.noThanksButton}
              onClick={handleDoNotSubscribeClick}
              data-testid="nothankscta"
            >
              {skipText}
            </MuiButton>
          </Box>
          {isDynamicHeightEnabled && (
            <NewsletterConsentStatement
              statement={newsletterConsentStatement}
            />
          )}
        </Container>
      </Grid>
    </div>
  );
};

export default Newsletter;
